/**
 * Icons.
 *
 * Inline SVG rather than an icon font or package: about 3 KB total, no extra
 * request, and they inherit `currentColor` so dark mode needs no work.
 *
 * All use a 24×24 viewBox with 1.8px strokes, which stays legible at the 22px
 * the tab bar renders them at.
 */

import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement>;

function Svg({ children, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const IconHome = (p: IconProps) => (
  <Svg {...p}><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V20h14V9.5" /><path d="M9.5 20v-6h5v6" /></Svg>
);

export const IconCatalogue = (p: IconProps) => (
  <Svg {...p}><rect x="3" y="4" width="7" height="7" rx="1.5" /><rect x="14" y="4" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></Svg>
);

export const IconCart = (p: IconProps) => (
  <Svg {...p}><path d="M3 4h2.2l2.3 11.2a1.5 1.5 0 0 0 1.5 1.2h8.6a1.5 1.5 0 0 0 1.5-1.2L21 7H6" /><circle cx="9.5" cy="20" r="1.4" /><circle cx="17.5" cy="20" r="1.4" /></Svg>
);

export const IconOrders = (p: IconProps) => (
  <Svg {...p}><path d="M7 3h7l4 4v14H7z" /><path d="M14 3v4h4" /><path d="M10 12h6M10 16h4" /></Svg>
);

export const IconRupee = (p: IconProps) => (
  <Svg {...p}><path d="M7 4h10M7 8.5h10" /><path d="M14.5 4c0 4-3 4.5-7.5 4.5" /><path d="M7 8.5c6 0 7 3 4.5 5.5L16 20" /></Svg>
);

export const IconChat = (p: IconProps) => (
  <Svg {...p}><path d="M4 5h16v11H8l-4 3.5z" /><path d="M8.5 10h7" /></Svg>
);

export const IconCalendar = (p: IconProps) => (
  <Svg {...p}><rect x="3.5" y="5" width="17" height="16" rx="2" /><path d="M3.5 10h17M8 3v4M16 3v4" /></Svg>
);

export const IconRoute = (p: IconProps) => (
  <Svg {...p}><circle cx="6" cy="6" r="2.4" /><circle cx="18" cy="18" r="2.4" /><path d="M6 8.5v5A4.5 4.5 0 0 0 10.5 18H15" /></Svg>
);

export const IconTruck = (p: IconProps) => (
  <Svg {...p}><path d="M2.5 6.5h11v9h-11z" /><path d="M13.5 10h4l3.5 3v2.5h-7.5" /><circle cx="6.5" cy="18" r="1.8" /><circle cx="17" cy="18" r="1.8" /></Svg>
);

export const IconUsers = (p: IconProps) => (
  <Svg {...p}><circle cx="9" cy="8" r="3.2" /><path d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" /><path d="M16 5.2a3.2 3.2 0 0 1 0 6.1M18 20c0-2.3-.9-4-2.4-5" /></Svg>
);

export const IconDashboard = (p: IconProps) => (
  <Svg {...p}><path d="M4 19V11M9.3 19V5M14.7 19v-6M20 19V8" /></Svg>
);

export const IconCheckCircle = (p: IconProps) => (
  <Svg {...p}><circle cx="12" cy="12" r="8.5" /><path d="M8.5 12.3l2.4 2.4 4.6-4.9" /></Svg>
);

export const IconBox = (p: IconProps) => (
  <Svg {...p}><path d="M12 3l8 4.2v9.6L12 21l-8-4.2V7.2z" /><path d="M4 7.2l8 4.2 8-4.2M12 11.4V21" /></Svg>
);

export const IconReport = (p: IconProps) => (
  <Svg {...p}><rect x="4" y="3.5" width="16" height="17" rx="2" /><path d="M8 9h8M8 13h8M8 17h5" /></Svg>
);

export const IconSettings = (p: IconProps) => (
  <Svg {...p}><circle cx="12" cy="12" r="3" /><path d="M12 3v2.2M12 18.8V21M4.6 7.5l1.9 1.1M17.5 15.4l1.9 1.1M4.6 16.5l1.9-1.1M17.5 8.6l1.9-1.1" /></Svg>
);

export const IconMore = (p: IconProps) => (
  <Svg {...p}><circle cx="5.5" cy="12" r="1.4" /><circle cx="12" cy="12" r="1.4" /><circle cx="18.5" cy="12" r="1.4" /></Svg>
);

export const IconBack = (p: IconProps) => (
  <Svg {...p}><path d="M15 5l-7 7 7 7" /></Svg>
);

export const IconClose = (p: IconProps) => (
  <Svg {...p}><path d="M6 6l12 12M18 6L6 18" /></Svg>
);

export const IconPhone = (p: IconProps) => (
  <Svg {...p}><path d="M5 3.5h3.2l1.6 4-2 1.5a11 11 0 0 0 5.7 5.7l1.5-2 4 1.6V19a1.5 1.5 0 0 1-1.7 1.5C10.6 19.8 4.2 13.4 3.5 5.2A1.5 1.5 0 0 1 5 3.5z" /></Svg>
);

export const IconPin = (p: IconProps) => (
  <Svg {...p}><path d="M12 21s6.5-6 6.5-11a6.5 6.5 0 1 0-13 0C5.5 15 12 21 12 21z" /><circle cx="12" cy="10" r="2.4" /></Svg>
);

export const IconCamera = (p: IconProps) => (
  <Svg {...p}><path d="M4 8h3l1.5-2h7L17 8h3v11H4z" /><circle cx="12" cy="13.5" r="3.2" /></Svg>
);

export const IconSearch = (p: IconProps) => (
  <Svg {...p}><circle cx="11" cy="11" r="6.5" /><path d="M16 16l4.5 4.5" /></Svg>
);

export const IconAlert = (p: IconProps) => (
  <Svg {...p}><path d="M12 4l8.5 15h-17z" /><path d="M12 10v4M12 16.6v.1" /></Svg>
);

/** A no-entry / stop sign — a circle with a diagonal bar. Used for a blacklisted
 *  customer, so field staff see "no sale" at a glance. */
export const IconBan = (p: IconProps) => (
  <Svg {...p}><circle cx="12" cy="12" r="9" /><path d="M5.64 5.64l12.72 12.72" /></Svg>
);

export const IconOffline = (p: IconProps) => (
  <Svg {...p}><path d="M3 3l18 18" /><path d="M8.5 15.5a5 5 0 0 1 7 0" /><path d="M5 12a9.5 9.5 0 0 1 3.5-2.3M19 12a9.5 9.5 0 0 0-6.8-2.9" /><path d="M12 19v.1" /></Svg>
);

export const IconSync = (p: IconProps) => (
  <Svg {...p}><path d="M20 6.5A8 8 0 0 0 5.2 8.8" /><path d="M4 17.5A8 8 0 0 0 18.8 15.2" /><path d="M4 4v5h5M20 20v-5h-5" /></Svg>
);

export const IconLogout = (p: IconProps) => (
  <Svg {...p}><path d="M14 4H6v16h8" /><path d="M17 8.5l3.5 3.5L17 15.5M20.5 12H10" /></Svg>
);

export const IconPlus = (p: IconProps) => (
  <Svg {...p}><path d="M12 5v14M5 12h14" /></Svg>
);

export const IconChevron = (p: IconProps) => (
  <Svg {...p}><path d="M9 5l7 7-7 7" /></Svg>
);

export const IconInbox = (p: IconProps) => (
  <Svg {...p}><path d="M3.5 13.5 6 5h12l2.5 8.5V19h-17z" /><path d="M3.5 13.5H9a3 3 0 0 0 6 0h5.5" /></Svg>
);

export const IconSend = (p: IconProps) => (
  <Svg {...p}><path d="M4 12l16-7-7 16-2.2-6.8z" /></Svg>
);

export const IconImage = (p: IconProps) => (
  <Svg {...p}><rect x="3.5" y="5" width="17" height="14" rx="2" /><circle cx="9" cy="10" r="1.6" /><path d="M4 17l5-4.5 4 3 3-2.5 4 4" /></Svg>
);

/**
 * WhatsApp.
 *
 * Drawn in the same stroked style as every other icon here rather than using
 * Meta's official glyph: their brand assets are licensed for specific uses and
 * a filled green logo would be the only branded mark in the app. The speech
 * bubble with a handset reads as WhatsApp in context — it sits directly above
 * the words "Open WhatsApp".
 */
export const IconWhatsApp = (p: IconProps) => (
  <Svg {...p}>
    <path d="M20.5 11.7a8.5 8.5 0 0 1-12.6 7.4L3.5 20.5l1.4-4.4A8.5 8.5 0 1 1 20.5 11.7z" />
    <path d="M9.2 8.6c-.3 0-.6.1-.8.4-.3.3-.7.8-.7 1.6 0 .9.5 1.8 1.4 2.9.9 1 2 1.7 3 2 .8.2 1.4.1 1.8-.2.3-.2.6-.6.7-1 .1-.3 0-.5-.2-.6l-1.4-.7c-.2-.1-.4 0-.5.2l-.3.5c-.1.2-.3.2-.5.1-.5-.2-1-.6-1.4-1.1-.4-.5-.5-.8-.3-1l.3-.4c.1-.2.1-.3 0-.5l-.5-1.4c-.1-.2-.2-.3-.4-.3z" />
  </Svg>
);
